// Campos do painel do editor de landing pages. Controlados e sem estado próprio (o editor guarda tudo).
import { useId, type ReactNode } from 'react'
import { ArrowDown, ArrowUp, Plus, Trash2, X } from 'lucide-react'
import type { ButtonAction, LandingButton, LandingImage, SectionBg } from '@impulso/shared/landing'
import { Switch } from '../../ui'
import ImagePicker from '../assets/ImagePicker'
import { useAssets } from '../assets/api'

export function Field({ label, hint, children, htmlFor }: { label: string; hint?: ReactNode; children: ReactNode; htmlFor?: string }) {
  return (
    <div className="field">
      <label htmlFor={htmlFor}>{label}</label>
      {children}
      {hint && <span className="small muted">{hint}</span>}
    </div>
  )
}

export function TextField({ label, value, onChange, placeholder, max = 200, hint, type = 'text', inputMode }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; max?: number; hint?: ReactNode; type?: string; inputMode?: 'text' | 'tel' | 'url' | 'numeric'
}) {
  const id = useId()
  return (
    <Field label={label} hint={hint} htmlFor={id}>
      <input id={id} className="input" type={type} inputMode={inputMode} value={value} placeholder={placeholder} maxLength={max} onChange={(e) => onChange(e.target.value)} />
    </Field>
  )
}

export function AreaField({ label, value, onChange, placeholder, max = 3000, rows = 4, hint }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; max?: number; rows?: number; hint?: ReactNode
}) {
  const id = useId()
  return (
    <Field label={label} hint={hint} htmlFor={id}>
      <textarea id={id} className="textarea" rows={rows} value={value} placeholder={placeholder} maxLength={max} onChange={(e) => onChange(e.target.value)} />
    </Field>
  )
}

export function Choice<T extends string | number>({ label, value, onChange, options }: { label: string; value: T; onChange: (v: T) => void; options: Array<{ value: T; label: string }> }) {
  return (
    <div className="field">
      <label>{label}</label>
      <div className="seg" role="radiogroup" aria-label={label}>
        {options.map((o) => (
          <button key={String(o.value)} type="button" role="radio" aria-checked={value === o.value} className={value === o.value ? 'on' : ''} onClick={() => onChange(o.value)}>{o.label}</button>
        ))}
      </div>
    </div>
  )
}

export function Toggle({ label, value, onChange, hint }: { label: string; value: boolean; onChange: (v: boolean) => void; hint?: string }) {
  return (
    <div className="row between" style={{ gap: 12 }}>
      <div><div className="small strong">{label}</div>{hint && <div className="small muted">{hint}</div>}</div>
      <Switch on={value} onChange={onChange} label={label} />
    </div>
  )
}

export function BgField({ value, onChange }: { value: SectionBg | undefined; onChange: (v: SectionBg) => void }) {
  return <Choice label="Fundo da seção" value={value ?? 'default'} onChange={onChange} options={[{ value: 'default', label: 'Padrão' }, { value: 'soft', label: 'Suave' }, { value: 'primary', label: 'Cor' }, { value: 'dark', label: 'Escuro' }]} />
}

const ACTIONS: Array<{ value: ButtonAction['kind']; label: string }> = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'phone', label: 'Ligar' },
  { value: 'form', label: 'Formulário' },
  { value: 'link', label: 'Link' },
]

function defaultAction(kind: ButtonAction['kind'], phone: string): ButtonAction {
  switch (kind) {
    case 'whatsapp': return { kind, phone, message: 'Olá! Vi o anúncio e quero saber mais.' }
    case 'phone': return { kind, phone }
    case 'link': return { kind, url: 'https://', newTab: true }
    case 'form': return { kind }
  }
}

/** Botão: texto + o que acontece ao clicar. */
export function ButtonField({ label, value, onChange, optional, defaultPhone }: { label: string; value: LandingButton | null; onChange: (v: LandingButton | null) => void; optional?: boolean; defaultPhone: string }) {
  if (!value) {
    return (
      <div className="field">
        <label>{label}</label>
        <button type="button" className="btn sm" style={{ alignSelf: 'flex-start' }} onClick={() => onChange({ label: 'Chamar no WhatsApp', action: defaultAction('whatsapp', defaultPhone) })}><Plus size={14} /> Adicionar botão</button>
      </div>
    )
  }
  const a = value.action
  const set = (patch: Partial<LandingButton>) => onChange({ ...value, ...patch })
  return (
    <div className="sub-card stack" style={{ gap: 10 }}>
      <div className="row between"><span className="small strong">{label}</span>{optional && <button type="button" className="btn ghost sm" onClick={() => onChange(null)} aria-label="Remover botão"><X size={14} /> Remover</button>}</div>
      <TextField label="Texto do botão" value={value.label} max={80} onChange={(v) => set({ label: v })} />
      <Choice label="Ao clicar" value={a.kind} onChange={(k) => set({ action: defaultAction(k, 'phone' in a ? a.phone : defaultPhone) })} options={ACTIONS} />
      {(a.kind === 'whatsapp' || a.kind === 'phone') && (
        <TextField label={a.kind === 'whatsapp' ? 'Número do WhatsApp' : 'Telefone'} inputMode="tel" value={a.phone} placeholder="(11) 99999-9999" max={30} onChange={(v) => set({ action: { ...a, phone: v } })}
          hint={a.phone.replace(/\D/g, '').length < 10 ? 'Coloque o DDD. Ex.: (11) 99999-9999' : undefined} />
      )}
      {a.kind === 'whatsapp' && <AreaField label="Mensagem que já vem escrita" rows={2} max={500} value={a.message} onChange={(v) => set({ action: { ...a, message: v } })} />}
      {a.kind === 'link' && (
        <>
          <TextField label="Endereço (link)" inputMode="url" value={a.url} max={1000} onChange={(v) => set({ action: { ...a, url: v } })} hint={a.url && !/^(https?:\/\/|mailto:|#)/i.test(a.url) ? 'O link precisa começar com https://' : undefined} />
          <Toggle label="Abrir em nova aba" value={a.newTab} onChange={(v) => set({ action: { ...a, newTab: v } })} />
        </>
      )}
      {a.kind === 'form' && <span className="small muted">Leva até o formulário da página (adicione um bloco "Formulário de contato").</span>}
    </div>
  )
}

export function ImageField({ label, companyId, value, onChange, optional = true, canEdit }: { label: string; companyId: string; value: LandingImage | null; onChange: (v: LandingImage | null) => void; optional?: boolean; canEdit: boolean }) {
  return (
    <div className="field">
      <div className="row between"><label>{label}</label>{optional && value && <button type="button" className="btn ghost sm" onClick={() => onChange(null)}><X size={14} /> Sem imagem</button>}</div>
      <ImagePicker companyId={companyId} canEdit={canEdit} value={value?.assetId ?? null} onChange={(id) => onChange({ assetId: id, alt: value?.alt ?? '' })} />
      {value?.assetId && <TextField label="Descrição da imagem (para acessibilidade e Google)" value={value.alt} max={200} onChange={(v) => onChange({ ...value, alt: v })} />}
    </div>
  )
}

/** Várias imagens: clique em uma imagem da biblioteca para adicionar; reordene ou remova abaixo. */
export function GalleryField({ companyId, value, onChange, canEdit }: { companyId: string; value: LandingImage[]; onChange: (v: LandingImage[]) => void; canEdit: boolean }) {
  const assets = useAssets(companyId)
  const urls = new Map((assets.data?.items ?? []).map((a) => [a.id, a.url]))
  const chosen = value.filter((i) => i.assetId)
  return (
    <div className="stack" style={{ gap: 10 }}>
      <div className="field">
        <label>Fotos da galeria ({chosen.length}/12)</label>
        {chosen.length === 0 && <span className="small muted">Clique nas imagens abaixo para adicionar.</span>}
        <div className="row wrap" style={{ gap: 8 }}>
          {value.map((img, i) => img.assetId && (
            <div key={`${img.assetId}-${i}`} className="gal-item">
              {urls.get(img.assetId) ? <img src={urls.get(img.assetId)} alt="" /> : <span className="skeleton" style={{ width: '100%', height: '100%' }} />}
              <div className="gal-actions">
                <button type="button" aria-label="Mover para a esquerda" disabled={i === 0} onClick={() => onChange(move(value, i, -1))}><ArrowUp size={12} style={{ transform: 'rotate(-90deg)' }} /></button>
                <button type="button" aria-label="Remover" onClick={() => onChange(value.filter((_, j) => j !== i))}><Trash2 size={12} /></button>
                <button type="button" aria-label="Mover para a direita" disabled={i === value.length - 1} onClick={() => onChange(move(value, i, 1))}><ArrowDown size={12} style={{ transform: 'rotate(-90deg)' }} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {chosen.length < 12 && (
        <div className="field">
          <label>Biblioteca de imagens</label>
          <ImagePicker companyId={companyId} canEdit={canEdit} value={null} onChange={(id) => { if (id) onChange([...value.filter((v) => v.assetId), { assetId: id, alt: '' }]) }} />
        </div>
      )}
    </div>
  )
}

export function move<T>(list: T[], i: number, dir: -1 | 1): T[] {
  const j = i + dir
  if (j < 0 || j >= list.length) return list
  const next = list.slice()
  ;[next[i], next[j]] = [next[j]!, next[i]!]
  return next
}

/** Lista editável (itens de benefícios, depoimentos, FAQ...). */
export function ListField<T>({ label, items, onChange, render, create, max = 12, itemTitle }: {
  label: string; items: T[]; onChange: (v: T[]) => void; render: (item: T, update: (patch: Partial<T>) => void) => ReactNode; create: () => T; max?: number; itemTitle: (item: T, i: number) => string
}) {
  return (
    <div className="field">
      <label>{label}</label>
      <div className="stack" style={{ gap: 8 }}>
        {items.map((item, i) => (
          <details key={i} className="sub-card" open={items.length <= 2}>
            <summary className="row between" style={{ gap: 6 }}>
              <span className="small strong" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}>{itemTitle(item, i) || `Item ${i + 1}`}</span>
              <span className="row" style={{ gap: 2 }} onClick={(e) => e.preventDefault()}>
                <button type="button" className="mini-btn" aria-label="Subir" disabled={i === 0} onClick={() => onChange(move(items, i, -1))}><ArrowUp size={13} /></button>
                <button type="button" className="mini-btn" aria-label="Descer" disabled={i === items.length - 1} onClick={() => onChange(move(items, i, 1))}><ArrowDown size={13} /></button>
                <button type="button" className="mini-btn" aria-label="Remover" onClick={() => onChange(items.filter((_, j) => j !== i))}><Trash2 size={13} /></button>
              </span>
            </summary>
            <div className="stack mt-8" style={{ gap: 10 }}>{render(item, (patch) => onChange(items.map((x, j) => (j === i ? { ...x, ...patch } : x))))}</div>
          </details>
        ))}
        {items.length < max && <button type="button" className="btn sm" style={{ alignSelf: 'flex-start' }} onClick={() => onChange([...items, create()])}><Plus size={14} /> Adicionar</button>}
      </div>
    </div>
  )
}

/** Lista simples de textos (tópicos). */
export function LinesField({ label, value, onChange, max = 12, placeholder }: { label: string; value: string[]; onChange: (v: string[]) => void; max?: number; placeholder?: string }) {
  return (
    <div className="field">
      <label>{label}</label>
      <div className="stack" style={{ gap: 6 }}>
        {value.map((v, i) => (
          <div key={i} className="row" style={{ gap: 6 }}>
            <input className="input" value={v} maxLength={160} placeholder={placeholder} onChange={(e) => onChange(value.map((x, j) => (j === i ? e.target.value : x)))} aria-label={`${label} ${i + 1}`} />
            <button type="button" className="mini-btn" aria-label="Remover" onClick={() => onChange(value.filter((_, j) => j !== i))}><Trash2 size={13} /></button>
          </div>
        ))}
        {value.length < max && <button type="button" className="btn sm" style={{ alignSelf: 'flex-start' }} onClick={() => onChange([...value, ''])}><Plus size={14} /> Adicionar</button>}
      </div>
    </div>
  )
}
